import { NavigationSection } from '@/components/Navigation'

export interface SearchResult {
  url: string
  title: string
  pageTitle?: string
  [key: string]: string | undefined
}

// Must stay in sync with the slugify used to generate heading ids in
// src/lib/markdoc-config.tsx — otherwise section anchors won't resolve.
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

interface Section {
  url: string
  title: string
  pageTitle?: string
  haystack: string // lowercased searchable text (title + body)
}

// Load every page's raw markdown at build time so we can index page content,
// not just navigation titles.
const rawFiles = import.meta.glob('/src/app/**/page.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

// /src/app/docs/installation/page.md -> /docs/installation ; /src/app/page.md -> /
function fileToRoute(file: string): string {
  const route = file.replace('/src/app', '').replace(/\/page\.md$/, '')
  return route === '' ? '/' : route
}

// Reduce a heading's markdown to its plain text — matches what collectText()
// produces in markdoc-config.tsx, so slugify() yields the same id.
function cleanHeading(text: string): string {
  return text
    .replace(/`([^`]*)`/g, '$1') // inline code
    .replace(/\*\*([^*]*)\*\*/g, '$1') // bold
    .replace(/\*([^*]*)\*/g, '$1') // italic
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> text
    .trim()
}

function buildSections(): Section[] {
  const sections: Section[] = []

  for (const [file, raw] of Object.entries(rawFiles)) {
    const route = fileToRoute(file)
    const lines = raw.split('\n')

    // Parse + skip frontmatter, capturing the page title.
    let i = 0
    let pageTitle = ''
    if (lines[0]?.trim() === '---') {
      const end = lines.findIndex((line, idx) => idx > 0 && line.trim() === '---')
      if (end > 0) {
        const frontmatter = lines.slice(1, end).join('\n')
        pageTitle = frontmatter.match(/^title:\s*(.+)$/m)?.[1]?.trim() ?? ''
        i = end + 1
      }
    }

    // Page-level section (no anchor).
    let current: Section = {
      url: route,
      title: pageTitle || route,
      pageTitle: undefined,
      haystack: `${pageTitle}\n`.toLowerCase(),
    }
    sections.push(current)

    let inFence = false
    for (; i < lines.length; i++) {
      const line = lines[i]

      if (/^\s*(```|~~~)/.test(line)) {
        inFence = !inFence
        current.haystack += line.toLowerCase() + '\n'
        continue
      }

      const heading = inFence ? null : line.match(/^(#{1,6})\s+(.*)$/)
      // Open a new searchable section for h1–h3 (each has its own anchor).
      if (heading && heading[1].length <= 3) {
        const titleText = cleanHeading(heading[2])
        const hash = slugify(titleText)
        current = {
          url: route + (hash ? `#${hash}` : ''),
          title: titleText,
          pageTitle: pageTitle || undefined,
          haystack: `${titleText}\n`.toLowerCase(),
        }
        sections.push(current)
      } else {
        current.haystack += line.toLowerCase() + '\n'
      }
    }
  }

  return sections
}

const SECTIONS = buildSections()

// Full-text search across page content. Every whitespace-separated term must
// appear somewhere in a section; results are ranked by how well the section
// title matches, with content-only matches ranked below title matches.
export function search(
  query: string,
  _navigationItems?: NavigationSection[],
  options: { limit?: number } = {},
): SearchResult[] {
  const { limit = 10 } = options
  const normalized = query.trim().toLowerCase()

  if (!normalized) {
    return []
  }

  const terms = normalized.split(/\s+/).filter(Boolean)

  const scored: { section: Section; score: number }[] = []

  for (const section of SECTIONS) {
    if (!terms.every((term) => section.haystack.includes(term))) {
      continue
    }

    const titleLc = section.title.toLowerCase()
    let score = 1 // base: content match

    if (titleLc === normalized) {
      score += 100
    } else if (titleLc.startsWith(normalized)) {
      score += 60
    } else if (titleLc.includes(normalized)) {
      score += 40
    }

    for (const term of terms) {
      if (titleLc.includes(term)) score += 10
    }

    scored.push({ section, score })
  }

  scored.sort(
    (a, b) => b.score - a.score || a.section.title.localeCompare(b.section.title),
  )

  // De-duplicate by URL and cap to the limit.
  const seen = new Set<string>()
  const results: SearchResult[] = []

  for (const { section } of scored) {
    if (seen.has(section.url)) continue
    seen.add(section.url)
    results.push({
      url: section.url,
      title: section.title,
      pageTitle: section.pageTitle,
    })
    if (results.length >= limit) break
  }

  return results
}
